// Templates → Graphic Plates: every animated plate design, playing its loop —
// Real 3D (with plates made from a description), the channel's own uploaded
// plates, and Cartoon 2D.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiLayers, FiX, FiChevronLeft, FiChevronRight, FiRepeat, FiSquare, FiLoader, FiPlus, FiTrash2, FiAlertTriangle, FiZap, FiVideo, FiVideoOff, FiUpload, FiSliders, FiFileText, FiMessageSquare, FiPackage, FiPaperclip, FiSearch, FiTag, FiCalendar, FiUser, FiRotateCcw, FiStar } from 'react-icons/fi';
import { fullStoryVideoService } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { useAuthStore } from '../../utils/store';
import { showError } from '../FullStory/shared';
import { UploadDialog, PlateSetup, FontPicker, PromptPanel, PlateThread } from './UploadedPlates';
import { useNow, since, etaText, setEstimates } from './plateTime';
import { PromptLibrary, FavoriteButton } from './PlatePrompts';
import { PlateLive } from './PlateLive';
import { PlateStudio } from './PlateStudio';
import { PackageDialog, PlateDownloads } from './PlatePackage';

const POLL_MS = 4000;
const POLL_FAST_MS = 1500;
const MANAGERS = ['admin', 'superadmin', 'manager'];
const BUSY = ['planning', 'imaging', 'drawing', 'designing'];
// A plate's description, at most (the server checks the same).
const MAX_PROMPT = 10000;
// "Create graphic plate": optional reference pictures (the prompt is required).
const MAX_CREATE_REFERENCES = 5;
const REFERENCE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Made with "Create graphic plate": a finished design (created) or an older Real 3D one (custom). */
const isMade = (tp) => Boolean(tp.custom || tp.created);

/** Whether a template belongs on a tab: "Created Plates" holds the plates made from a prompt. */
export const inStyle = (tp, style) => (style === 'created' ? isMade(tp) : tp.style === style && !isMade(tp));

/** Still being made, or a change asked for in its thread is being made. */
export const isBusy = (tp) => (isMade(tp) && (BUSY.includes(tp.status) || Boolean(tp.edits?.some((e) => e.status === 'working'))))
  || tp.people?.status === 'working';

// A made plate has a poster once its picture is made.
const hasPoster = (tp) => !isMade(tp) || tp.status === 'drawing' || tp.status === 'ready';

// Cards start their loops a few at a time: a screen of cards all downloading
// at once share the browser's six connections with the posters, so every
// card waits. A card holds a slot until its loop can play.
const MAX_LOADING = 4;
let freeSlots = MAX_LOADING;
const waiting = [];
function releaseSlot() {
  const next = waiting.shift();
  if (next) next(); else freeSlots++;
}
/** grant() now if a slot is free, else when one is; returns a cancel for a queued request. */
function requestSlot(grant) {
  if (freeSlots > 0) { freeSlots--; grant(); return null; }
  waiting.push(grant);
  return () => { const i = waiting.indexOf(grant); if (i >= 0) waiting.splice(i, 1); };
}
// For tests.
export const _slots = { requestSlot, releaseSlot, free: () => freeSlots, waiting: () => waiting.length };

/** A template's loop (its card-sized copy), playing only while it is on screen (posters until then). */
/** A plate still being made (its time so far; usually 1–2 minutes), or one that failed (why). */
const MakingCard = ({ template, innerRef, className = '' }) => {
  const t = useT();
  const failed = template.status === 'failed';
  const now = useNow(!failed);
  return (
    // (Placed by the caller — absolute in the viewer — or in the flow, as a card.)
    <div ref={innerRef} className={`${/absolute/.test(className) ? '' : 'relative '}bg-gradient-to-br from-gray-800 to-gray-950 flex flex-col items-center justify-center gap-2 text-center px-4 ${className}`}>
      {failed ? <FiAlertTriangle size={22} className="text-amber-400" /> : <FiLoader size={22} className="text-white/70 animate-spin" />}
      <p className="text-sm text-white/85">{t(`plateTemplates.status.${template.status}`)}</p>
      {!failed && template.created && (
        <p className="text-xs text-white/55 tabular-nums">{etaText(t, template.designStartedAt || template.createdAt, now, 'design')}</p>
      )}
      {failed && template.error && <p className="text-xs text-white/55">{template.error}</p>}
      {failed && template.created && <p className="text-[11px] text-white/45">{t('plateTemplates.openToRetry')}</p>}
    </div>
  );
};

/** "Preparing animation…" on a card — for a made plate with its time so far, as its making showed it. */
const Preparing = ({ template }) => {
  const t = useT();
  // (Only while it is being made: a plate redrawn later — a new setting — shows no old clock.)
  const timed = Boolean(template.created && template.designStartedAt && !template.animatedAt
    && Date.now() - Date.parse(template.designStartedAt) < 15 * 60 * 1000);
  const now = useNow(timed);
  return (
    <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5">
      <FiLoader size={11} className="animate-spin" /> {t('plateTemplates.preparing')}
      {timed && (
        <span className="tabular-nums text-white/75">
          {/* The whole making so far; what's left of the animation (after the design, if it isn't ready yet). */}
          {template.readyAt
            ? `${since(template.designStartedAt, now)} · ${etaText(t, template.readyAt, now, 'animation').split(' · ')[1]}`
            : etaText(t, template.designStartedAt, now, 'design', 15)}
        </span>
      )}
    </span>
  );
};

export const LoopPreview = ({ template, ready, failed = false, className = '' }) => {
  const t = useT();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [granted, setGranted] = useState(false);
  const holding = useRef(false);
  const release = useCallback(() => {
    if (holding.current) { holding.current = false; releaseSlot(); }
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!(ready && visible)) { setGranted(false); return undefined; }
    const cancel = requestSlot(() => { holding.current = true; setGranted(true); });
    return () => { if (cancel) cancel(); release(); };
  }, [ready, visible, release]);

  if (!hasPoster(template)) {
    return <MakingCard template={template} innerRef={ref} className={className} />;
  }
  const poster = fullStoryVideoService.plateTemplateUrl(template.id, 'poster.jpg', template.updatedAt);
  return (
    <div ref={ref} className={`${/absolute/.test(className) ? '' : 'relative '}bg-gray-900 ${className}`}>
      {ready && visible && granted ? (
        <video
          key="loop"
          src={fullStoryVideoService.plateTemplateUrl(template.id, 'card.mp4', template.updatedAt)}
          poster={poster}
          onCanPlay={release}
          onError={release}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img src={poster} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      )}
      {!ready && !failed && <Preparing template={template} />}
    </div>
  );
};

/**
 * The template played with sample footage in its windows. The server makes
 * it the first time (about a minute); until then this polls its status.
 */
const usePreview = (templateId, enabled, version) => {
  const [state, setState] = useState({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) { setState({ status: 'idle' }); return undefined; }
    let cancelled = false; let timer = null;
    setState({ status: 'building' });
    const poll = async () => {
      try {
        const next = await fullStoryVideoService.plateTemplatePreview(templateId);
        if (cancelled) return;
        setState(next);
        if (next.status === 'building') timer = setTimeout(poll, 3000);
      } catch (e) {
        if (!cancelled) setState({ status: 'failed', error: e?.response?.data?.message || e.message });
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [templateId, enabled, attempt, version]);
  return [state, () => setAttempt((n) => n + 1)];
};

/** The large view of one template, with its details; arrows step through the list. */
const TemplateViewer = ({ template, ready, loopFailed = false, category, canRemove, canTag, onRemove, onEdit, onRestyle, onReload, onClose, onStep, withVideo, onToggleVideo }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const canPreview = ready;
  // An uploaded or created plate plays live (below): no server preview to wait for.
  const live = Boolean(template.uploaded && template.status !== 'failed' && template.status !== 'designing');
  const [preview, retry] = usePreview(template.id, withVideo && canPreview && !live, template.updatedAt);
  // An uploaded plate previews with sample words in its panel, not footage.
  const words = Boolean(template.uploaded);
  const [restyling, setRestyling] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [packaging, setPackaging] = useState(false);
  useEffect(() => { setShowPrompt(false); setShowThread(false); setPackaging(false); }, [template.id]);
  const canChange = Boolean(template.created && canRemove && template.status === 'ready');
  const canRetry = Boolean(template.created && canRemove && template.status === 'failed');
  // Its prompt can be kept as a favourite: a made plate's, or an uploaded plate's written prompt.
  const canFavorite = Boolean(template.uploaded && (template.created ? template.status === 'ready' : template.hasPrompt));
  const [retrying, setRetrying] = useState(false);
  const retryPlate = async () => {
    setRetrying(true);
    try {
      await fullStoryVideoService.retryCreatedPlate(template.id);
      await onReload();
    } catch (e) {
      showError(e);
    } finally {
      setRetrying(false);
    }
  };
  const [startingPrompt, setStartingPrompt] = useState(false);
  const writePrompt = async () => {
    setStartingPrompt(true);
    try {
      await fullStoryVideoService.writePlatePrompt(template.id);
      await onReload();
    } catch (e) {
      showError(e);
    } finally {
      setStartingPrompt(false);
    }
  };
  const restyle = async (change) => {
    setRestyling(true);
    try { await onRestyle(change); } finally { setRestyling(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      // Typing (the change thread, a name, a prompt): the keys move the cursor, not the gallery.
      // A package being written keeps the keys too.
      const el = e.target;
      if (packaging) return;
      if (el?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName)) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onStep(-1);
      if (e.key === 'ArrowRight') onStep(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep, packaging]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-5xl max-h-[96vh] overflow-y-auto bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative aspect-video bg-gray-900">
          {live ? (
            <PlateLive
              key={template.id}
              plateId={template.id}
              words={withVideo}
              version={template.updatedAt}
              poster={fullStoryVideoService.plateTemplateUrl(template.id, 'poster.jpg', template.updatedAt)}
            />
          ) : withVideo && preview.status === 'ready' ? (
            <video
              key={`${template.id}-preview`}
              src={fullStoryVideoService.plateTemplateUrl(template.id, 'preview.mp4', template.updatedAt)}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="absolute inset-0 w-full h-full"
            />
          ) : words && withVideo && preview.status === 'building' && ready ? (
            // The words in place at once (a picture, under a second) while their video is drawn.
            <img
              key={`${template.id}-words`}
              src={fullStoryVideoService.plateTemplateUrl(template.id, 'words.jpg', template.updatedAt)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : ready ? (
            <video
              key={template.id}
              src={fullStoryVideoService.plateTemplateUrl(template.id, 'loop.mp4', template.updatedAt)}
              poster={fullStoryVideoService.plateTemplateUrl(template.id, 'poster.jpg', template.updatedAt)}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <LoopPreview template={template} ready={false} failed={loopFailed} className="absolute inset-0" />
          )}
          {!live && withVideo && preview.status === 'building' && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center p-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-black/75 text-white text-xs px-3 py-1.5">
                <FiLoader size={13} className="animate-spin" /> {t(words ? 'plateTemplates.previewWordsBuilding' : 'plateTemplates.previewBuilding')}
              </span>
            </div>
          )}
          {!live && withVideo && preview.status === 'failed' && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center p-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-black/80 text-white text-xs px-3 py-1.5">
                <FiAlertTriangle size={13} className="text-amber-400" /> {preview.error || t('plateTemplates.previewFailed')}
                <button onClick={retry} className="underline ml-1">{t('plateTemplates.retry')}</button>
              </span>
            </div>
          )}
          {canPreview && (
            <button
              onClick={onToggleVideo}
              aria-pressed={withVideo}
              title={words
                ? t(withVideo ? 'plateTemplates.previewWordsOn' : 'plateTemplates.previewWordsOff')
                : t(withVideo ? 'plateTemplates.previewOn' : 'plateTemplates.previewOff')}
              className={`absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow transition-colors ${withVideo ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-black/60 text-white hover:bg-black/75'}`}
            >
              {withVideo ? <FiVideo size={14} /> : <FiVideoOff size={14} />} {t(words ? 'plateTemplates.previewWords' : 'plateTemplates.previewVideo')}
            </button>
          )}
          <button onClick={() => onStep(-1)} aria-label={t('plateTemplates.previous')} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
            <FiChevronLeft size={20} />
          </button>
          <button onClick={() => onStep(1)} aria-label={t('plateTemplates.next')} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
            <FiChevronRight size={20} />
          </button>
        </div>
        {showThread && canChange && <PlateThread plate={template} onChanged={onReload} />}
        {showPrompt && <PromptPanel templateId={template.id} />}
        {!template.hasPrompt && template.promptStatus === 'failed' && template.promptError && canRemove && (
          <p className="px-5 pt-3 text-xs text-amber-700 flex items-center gap-1.5">
            <FiAlertTriangle size={13} /> {t('plateTemplates.promptFailed', { error: template.promptError })}
          </p>
        )}
        {words && canRemove && (
          <div className="px-5 pt-4">
            <FontPicker font={template.font} effect={template.effect} busy={restyling} onChange={restyle} />
          </div>
        )}
        {ready && (
          <div className="px-5 pt-4 flex flex-wrap items-center justify-between gap-3">
            <PlateDownloads template={template} />
            {words && (
              <button onClick={() => setPackaging(true)} className="btn btn-primary btn-sm flex items-center gap-1.5">
                <FiPackage size={15} /> {t('plateTemplates.pkg.open')}
              </button>
            )}
          </div>
        )}
        {template.created && template.references > 0 && (
          <div className="px-5 pt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-500 inline-flex items-center gap-1"><FiPaperclip size={12} /> {t('plateTemplates.createReferencesUsed')}</span>
            {Array.from({ length: template.references }, (_, k) => (
              <a key={k} href={fullStoryVideoService.plateTemplateUrl(template.id, `ref-create-${k + 1}.jpg`)} target="_blank" rel="noreferrer">
                <img src={fullStoryVideoService.plateTemplateUrl(template.id, `ref-create-${k + 1}.jpg`)} alt="" loading="lazy" className="h-10 w-10 rounded object-cover border border-gray-200" />
              </a>
            ))}
          </div>
        )}
        {packaging && <PackageDialog plate={template} onClose={() => setPackaging(false)} />}
        <div className="flex items-start justify-between gap-4 p-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-primary-600">
              {lang === 'te' ? category?.te : category?.name}
              {template.custom && <span className="ml-2 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5">{t('plateTemplates.customBadge')}</span>}
              {template.uploaded && <span className="ml-2 rounded-full bg-sky-100 text-sky-700 px-2 py-0.5">{t(template.created ? 'plateTemplates.createdBadge' : 'plateTemplates.uploadedBadge')}</span>}
            </p>
            <h2 className="text-lg font-bold text-gray-900">{template.te} {template.name && <span className={template.te ? 'text-gray-400 font-medium' : ''}>{template.te ? '· ' : ''}{template.name}</span>}</h2>
            {template.uploaded && template.createdByName && <p className="text-xs text-gray-500 mt-1">{t('plateTemplates.by', { name: template.createdByName })}</p>}
            <p className="text-sm text-gray-600 mt-1">{template.hint}</p>
            {template.createdAt && (
              <p className="text-xs text-gray-500 mt-1">
                {t('plateTemplates.card.created', { at: when(template.createdAt) })}
                {template.updatedAt && template.updatedAt !== template.createdAt ? ` · ${t('plateTemplates.card.modified', { at: when(template.updatedAt) })}` : ''}
              </p>
            )}
            {/* How long it took: ready to use (its picture), and its animation drawn. */}
            {template.created && template.readyAt && template.designStartedAt && (
              <p className="text-xs text-emerald-700 mt-1 tabular-nums">
                {t('plateTemplates.times.ready', { time: since(template.designStartedAt, Date.parse(template.readyAt)) })}
                {template.animatedAt ? ` · ${t('plateTemplates.times.animated', { time: since(template.designStartedAt, Date.parse(template.animatedAt)) })}` : ''}
              </p>
            )}
            <TagEditor template={template} canEdit={canTag} onSaved={onReload} />
            {withVideo && preview.status === 'ready' && preview.credits?.length > 0 && (
              <p className="text-[11px] text-gray-500 mt-2">
                {t('plateTemplates.footage')}{' '}
                {preview.credits.map((c, i) => (
                  <span key={c.pageUrl || i}>
                    {i > 0 && ' · '}
                    <a href={c.pageUrl} target="_blank" rel="noreferrer" className="underline hover:text-gray-700">{c.title}</a>
                    {c.author ? ` — ${c.author}` : ''}{c.license ? ` (${c.license})` : ''}
                  </span>
                ))}
              </p>
            )}
            {template.custom && template.prompt && <p className="text-xs text-gray-500 mt-2 italic">“{template.prompt}”{template.createdByName ? ` — ${t('plateTemplates.by', { name: template.createdByName })}` : ''}</p>}
            <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1"><FiRepeat size={11} /> {t('plateTemplates.loop', { seconds: template.seconds })}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1">
                <FiSquare size={11} /> {template.windows > 1 ? t('plateTemplates.areas', { count: template.windows }) : t('plateTemplates.area')}
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 font-mono">{template.id}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canFavorite && <FavoriteButton plateId={template.id} />}
            {canRetry && (
              <button onClick={retryPlate} disabled={retrying} className="btn btn-primary btn-sm flex items-center gap-1">
                {retrying ? <FiLoader size={15} className="animate-spin" /> : <FiRotateCcw size={15} />} {t('plateTemplates.tryAgain')}
              </button>
            )}
            {canChange && (
              <button onClick={() => setShowThread((v) => !v)} aria-expanded={showThread} className={`btn btn-sm flex items-center gap-1 ${showThread ? 'btn-primary' : 'btn-outline'}`}>
                <FiMessageSquare size={15} /> {t('plateTemplates.changeWithPrompt')}
              </button>
            )}
            {template.hasPrompt && (
              <button onClick={() => setShowPrompt((v) => !v)} aria-expanded={showPrompt} className={`btn btn-sm flex items-center gap-1 ${showPrompt ? 'btn-primary' : 'btn-outline'}`}>
                <FiFileText size={15} /> {t('plateTemplates.viewPrompt')}
              </button>
            )}
            {!template.hasPrompt && template.promptStatus === 'writing' && (
              <button disabled className="btn btn-outline btn-sm flex items-center gap-1">
                <FiLoader size={15} className="animate-spin" /> {t('plateTemplates.writingPrompt')}
              </button>
            )}
            {!template.hasPrompt && template.uploaded && !template.created && canRemove && ['none', 'failed'].includes(template.promptStatus) && (
              <button onClick={writePrompt} disabled={startingPrompt} title={template.promptError ? t('plateTemplates.promptFailed', { error: template.promptError }) : undefined} className="btn btn-outline btn-sm flex items-center gap-1">
                {startingPrompt ? <FiLoader size={15} className="animate-spin" /> : <FiFileText size={15} />} {t('plateTemplates.writePrompt')}
              </button>
            )}
            {canRemove && template.uploaded && (
              <button onClick={onEdit} className="btn btn-ghost btn-sm flex items-center gap-1"><FiSliders size={15} /> {t('plateTemplates.edit')}</button>
            )}
            {canRemove && (
              <button onClick={onRemove} className="btn btn-ghost btn-sm text-red-600 flex items-center gap-1"><FiTrash2 size={15} /> {t('plateTemplates.remove')}</button>
            )}
            <button onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Describe a plate → a custom Real 3D plate, made in the background. */
export const CreateDialog = ({ onClose, onCreated, categories = [], initialPrompt = '' }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('general');
  // Tags (optional), comma-separated: they help find the plate later.
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  // "Favourite & recent prompts": open, and a prompt picked from it.
  const [library, setLibrary] = useState(false);
  const usePrompt = (text) => {
    if (prompt.trim() && prompt.trim() !== text.trim() && !window.confirm(t('platePrompts.replaceConfirm'))) return;
    setPrompt(text.slice(0, MAX_PROMPT));
    setLibrary(false);
  };
  // Reference pictures (optional): [{ file, url }] (url: a preview, released when removed or closed).
  const [images, setImages] = useState([]);
  const shown = useRef([]);
  shown.current = images;
  useEffect(() => () => shown.current.forEach((i) => URL.revokeObjectURL(i.url)), []);
  const attach = (fileList) => {
    const picked = Array.from(fileList || []).filter((f) => REFERENCE_TYPES.includes(f.type));
    if (picked.length < (fileList?.length || 0)) showError(new Error(t('plateTemplates.referenceType')));
    const room = MAX_CREATE_REFERENCES - images.length;
    if (picked.length > room) showError(new Error(t('plateTemplates.referenceMax', { count: MAX_CREATE_REFERENCES })));
    setImages((now) => [...now, ...picked.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };
  const drop = (k) => setImages((now) => {
    URL.revokeObjectURL(now[k].url);
    return now.filter((_, j) => j !== k);
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onCreated(await fullStoryVideoService.createPlateTemplate(prompt, {
        name, category, tags: tags.split(',').map((x) => x.trim()).filter(Boolean), images: images.map((i) => i.file),
      }));
    } catch (err) {
      showError(err);
      setBusy(false);
    }
  };

  return (
    // On a short (laptop) screen the form scrolls inside itself — not the page behind — and its buttons stay in sight.
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overscroll-contain" onClick={onClose} role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-xl max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-white rounded-2xl shadow-2xl p-5 pb-0 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiZap size={18} className="text-primary-600" /> {t('plateTemplates.createTitle')}</h2>
          <button type="button" onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
        </div>
        <p className="text-sm text-gray-600">{t('plateTemplates.createHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">{t('plateTemplates.editName')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t('plateTemplates.createNamePlaceholder')} className="input mt-1 text-sm" />
          </label>
          {categories.length > 0 && (
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">{t('plateTemplates.uploadCategory')}</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1 text-sm">
                {categories.map((c) => <option key={c.id} value={c.id}>{lang === 'te' ? c.te : c.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <label className="block text-sm">
          <span className="text-gray-700 font-medium inline-flex items-center gap-1.5">
            <FiTag size={13} aria-hidden="true" /> {t('plateTemplates.createTags')} <span className="text-gray-400 font-normal">{t('plateTemplates.optional')}</span>
          </span>
          <input value={tags} onChange={(e) => setTags(e.target.value)} maxLength={400} placeholder={t('plateTemplates.tags.placeholder')} className="input mt-1 text-sm" />
          <span className="block text-[11px] text-gray-500 mt-0.5">{t('plateTemplates.createTagsHint')}</span>
        </label>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-700 font-medium">{t('plateTemplates.createPromptLabel')}</span>
          <button type="button" onClick={() => setLibrary(true)} className="btn btn-outline btn-sm flex items-center gap-1.5">
            <FiStar size={14} className="text-amber-500" /> {t('platePrompts.open')}
          </button>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          aria-label={t('plateTemplates.createPromptLabel')}
          placeholder={t('plateTemplates.createPlaceholder')}
          rows={8}
          maxLength={MAX_PROMPT}
          autoFocus
          className="input w-full text-sm resize-y"
        />
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <label className={`btn btn-outline btn-sm flex items-center gap-1.5 ${images.length >= MAX_CREATE_REFERENCES ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
              <FiPaperclip size={14} /> {t('plateTemplates.createReferences', { count: MAX_CREATE_REFERENCES })}
              <input
                type="file"
                accept={REFERENCE_TYPES.join(',')}
                multiple
                className="sr-only"
                disabled={images.length >= MAX_CREATE_REFERENCES}
                onChange={(e) => { attach(e.target.files); e.target.value = ''; }}
              />
            </label>
            <span className="text-xs text-gray-500">{t('plateTemplates.createReferencesHint', { count: images.length, max: MAX_CREATE_REFERENCES })}</span>
          </div>
          {images.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label={t('plateTemplates.references')}>
              {images.map((img, k) => (
                <li key={img.url} className="relative">
                  <img src={img.url} alt={img.file.name} className="h-16 w-16 rounded-lg object-cover border border-gray-200" />
                  <button type="button" onClick={() => drop(k)} aria-label={t('plateTemplates.referenceRemove')} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-black">
                    <FiX size={11} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-gray-500">{t('plateTemplates.createCost')}</p>
          <span className={`text-xs tabular-nums flex-shrink-0 ${prompt.length >= MAX_PROMPT ? 'text-red-600' : 'text-gray-400'}`}>
            {prompt.length.toLocaleString('en-IN')} / {MAX_PROMPT.toLocaleString('en-IN')}
          </span>
        </div>
        <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">{t('plateTemplates.cancel')}</button>
          <button type="submit" disabled={busy || prompt.trim().length < 8} className="btn btn-primary flex items-center gap-2">
            {busy ? <FiLoader size={14} className="animate-spin" /> : <FiZap size={14} />}
            {busy ? t('plateTemplates.creating') : t('plateTemplates.createGo')}
          </button>
        </div>
      </form>
      {library && <PromptLibrary onUse={usePrompt} onClose={() => setLibrary(false)} />}
    </div>
  );
};

// The shared .input style sets a full width and roomy padding over Tailwind's
// utilities: compact controls (in a row) say so themselves.
const COMPACT = { width: 'auto', paddingTop: '0.25rem', paddingBottom: '0.25rem', paddingLeft: '0.5rem', paddingRight: '2rem' };

// The gallery shows this many cards at first, and this many more each time the
// end of the list scrolls into view — a long library opens at once.
const PAGE = 24;

/** A date and time as the cards show it: 2 Nov 2026, 01:45 pm. */
const when = (at) => (at ? new Date(at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/** Whether a plate matches what was typed: its name, tags, maker, category, description, id. */
export const matchesSearch = (tp, query, category) => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [tp.name, tp.te, tp.hint, tp.id, tp.createdByName, category?.name, category?.te, ...(tp.tags || [])].filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
};

/** The start and end (ms) of a date filter: 'today', '7', '30', or a custom from / to (yyyy-mm-dd). */
export const rangeOf = (range, from, to, now = Date.now()) => {
  const day = new Date(now); day.setHours(0, 0, 0, 0);
  if (range === 'today') return [day.getTime(), Infinity];
  if (range === '7' || range === '30') return [day.getTime() - (Number(range) - 1) * 86400000, Infinity];
  if (range === 'custom') {
    const start = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const end = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
    return [start, end];
  }
  return null;
};

/** A plate's tags, with adding and removing for those who may change them. */
const TagEditor = ({ template, canEdit, onSaved }) => {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const tags = template.tags || [];
  const saveTags = async (next) => {
    setBusy(true);
    try {
      await fullStoryVideoService.setPlateTags(template.id, next);
      setDraft('');
      await onSaved();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  const add = (e) => {
    e?.preventDefault();
    const fresh = draft.split(',').map((x) => x.trim()).filter(Boolean);
    if (fresh.length) saveTags([...tags, ...fresh]);
  };
  if (!canEdit && !tags.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <FiTag size={12} className="text-gray-400" aria-hidden="true" />
      {tags.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 text-[11px] px-2 py-0.5">
          {tag}
          {canEdit && (
            <button type="button" onClick={() => saveTags(tags.filter((x) => x !== tag))} disabled={busy} aria-label={t('plateTemplates.tags.remove', { tag })} className="text-gray-400 hover:text-red-600"><FiX size={11} /></button>
          )}
        </span>
      ))}
      {canEdit && tags.length < 12 && (
        <form onSubmit={add} className="inline-flex items-center gap-1">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={60} placeholder={t('plateTemplates.tags.placeholder')} aria-label={t('plateTemplates.tags.add')} style={{ ...COMPACT, width: '10rem', paddingRight: '0.5rem' }} className="input text-[11px]" />
          <button type="submit" disabled={busy || !draft.trim()} aria-label={t('plateTemplates.tags.add')} className="btn btn-ghost btn-sm text-[11px] px-1.5">{busy ? <FiLoader size={11} className="animate-spin" /> : <FiPlus size={11} />}</button>
        </form>
      )}
    </div>
  );
};

/** The gallery: every graphic plate template, by style and category. */
export const PlateTemplates = () => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  // Created Plates open first (the tabs: Created, Uploaded, Real 3D, Cartoon 2D).
  const [style, setStyle] = useState('created');
  const [category, setCategory] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settingUp, setSettingUp] = useState(null);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [withVideo, setWithVideo] = useState(false);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState('any');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [visible, setVisible] = useState(PAGE);
  const more = useRef(null);

  // Each load (done or failed) counts: a failed one still schedules the next check, so
  // waiting for a plate or an AI change never stops until the page is refreshed.
  const [loads, setLoads] = useState(0);
  const load = useCallback(async () => {
    try {
      const next = await fullStoryVideoService.plateTemplates();
      // How long each step usually takes here: the timers' estimates.
      setEstimates(next.estimates);
      setData(next);
    } catch (e) {
      showError(e);
    } finally {
      setLoads((n) => n + 1);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Loops are drawn on the server the first time, and custom plates take a few
  // minutes to make; keep asking until everything is ready.
  // A loop that couldn't be drawn (data.failed) shows its poster: nothing waits on it.
  const pending = data ? data.templates.filter((tp) => (isMade(tp) ? isBusy(tp) : !data.ready[tp.id] && !data.failed?.[tp.id])).length : 0;
  // An uploaded plate's prompt is written in the background (about a minute).
  const writingPrompts = Boolean(data?.templates.some((tp) => tp.promptStatus === 'writing'));
  // An AI change being made: checked more often, so its new version shows the moment it is ready.
  const changing = Boolean(data?.templates.some((tp) => tp.edits?.some((e) => e.status === 'working')));
  useEffect(() => {
    if (!data || (pending === 0 && !writingPrompts)) return undefined;
    const timer = setTimeout(load, changing ? POLL_FAST_MS : POLL_MS);
    return () => clearTimeout(timer);
  }, [data, loads, pending, writingPrompts, changing, load]);

  const ofStyle = useMemo(() => (data ? data.templates.filter((tp) => inStyle(tp, style)) : []), [data, style]);
  const byCategory = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c])), [data]);
  // Search and dates narrow the list (dates: when last changed — or made); newest change first, from the server.
  const shown = useMemo(() => {
    const span = rangeOf(range, from, to);
    return ofStyle.filter((tp) => {
      if (category !== 'all' && tp.category !== category) return false;
      if (!matchesSearch(tp, query, byCategory[tp.category])) return false;
      if (span) {
        const at = Date.parse(tp.updatedAt || tp.createdAt || '');
        if (Number.isNaN(at) || at < span[0] || at > span[1]) return false;
      }
      return true;
    });
  }, [ofStyle, category, query, range, from, to, byCategory]);
  // A new search or filter starts at the top of the list again.
  useEffect(() => { setVisible(PAGE); }, [style, category, query, range, from, to]);
  // More cards as the end of the list comes into view.
  useEffect(() => {
    const node = more.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible((v) => v + PAGE); }, { rootMargin: '600px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown.length, visible]);
  const open = shown.find((tp) => tp.id === openId);
  const canRemove = (tp) => (tp?.custom || tp?.uploaded) && (tp.createdBy === String(user?._id || user?.id) || MANAGERS.includes(user?.role));
  // Tags: a made plate's maker or a manager; the built-in plates, a manager.
  const canTag = (tp) => MANAGERS.includes(user?.role) || (!tp?.builtIn && tp?.createdBy === String(user?._id || user?.id));

  const step = useCallback((dir) => {
    setOpenId((current) => {
      const i = shown.findIndex((tp) => tp.id === current);
      return shown[(i + dir + shown.length) % shown.length]?.id ?? current;
    });
  }, [shown]);

  const remove = async (tp) => {
    if (!window.confirm(t(tp.uploaded ? 'plateTemplates.uploadRemoveConfirm' : 'plateTemplates.removeConfirm'))) return;
    try {
      await fullStoryVideoService.removePlateTemplate(tp.id);
      setOpenId(null);
      load();
    } catch (e) {
      showError(e);
    }
  };

  const label = (c) => (lang === 'te' ? c.te : c.name);

  return (
    <div className="space-y-5">
      <Link to="/full-story-video" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <FiArrowLeft size={14} /> {t('plateTemplates.back')}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
            <FiLayers size={20} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('plateTemplates.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('plateTemplates.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setUploading(true)} disabled={!data} className="btn btn-outline flex items-center gap-2">
            <FiUpload size={15} /> {t('plateTemplates.upload')}
          </button>
          <button onClick={() => setPromptsOpen(true)} className="btn btn-outline flex items-center gap-2">
            <FiStar size={15} className="text-amber-500" /> {t('platePrompts.galleryButton')}
          </button>
          <button onClick={() => setCreating(true)} className="btn btn-primary flex items-center gap-2">
            <FiPlus size={15} /> {t('plateTemplates.create')}
          </button>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-gray-500">{t('plateTemplates.loading')}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div role="tablist" className="flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
              {data.styles.map((s) => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={style === s.id}
                  onClick={() => { setStyle(s.id); setCategory('all'); }}
                  className={`px-3 py-1.5 rounded-md transition-colors ${style === s.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {label(s)} <span className="text-gray-400">{data.templates.filter((tp) => inStyle(tp, s.id)).length}</span>
                </button>
              ))}
            </div>
            <span className="text-sm text-gray-500 ml-auto">{t('plateTemplates.count', { count: ofStyle.length })}</span>
          </div>

          <div className="flex flex-wrap gap-2" role="tablist">
            {[{ id: 'all', name: t('plateTemplates.all'), te: t('plateTemplates.all') }, ...data.categories].map((c) => {
              const count = c.id === 'all' ? ofStyle.length : ofStyle.filter((tp) => tp.category === c.id).length;
              if (!count) return null;
              return (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={category === c.id}
                  onClick={() => setCategory(c.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === c.id ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  {label(c)} <span className={category === c.id ? 'text-white/70' : 'text-gray-400'}>{count}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="relative flex-1 min-w-[14rem] max-w-md">
              <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('plateTemplates.search')} aria-label={t('plateTemplates.search')} style={{ paddingLeft: '2.25rem' }} className="input text-sm py-1.5" />
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
              <FiCalendar size={14} className="text-gray-400" aria-hidden="true" />
              <select value={range} onChange={(e) => setRange(e.target.value)} aria-label={t('plateTemplates.dates.label')} style={COMPACT} className="input text-sm py-1.5 w-auto">
                {['any', 'today', '7', '30', 'custom'].map((r) => <option key={r} value={r}>{t(`plateTemplates.dates.${r}`)}</option>)}
              </select>
            </label>
            {range === 'custom' && (
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label={t('plateTemplates.dates.from')} style={COMPACT} className="input text-sm py-1 w-auto" />
                –
                <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label={t('plateTemplates.dates.to')} style={COMPACT} className="input text-sm py-1 w-auto" />
              </span>
            )}
            {(query || range !== 'any') && (
              <button type="button" onClick={() => { setQuery(''); setRange('any'); setFrom(''); setTo(''); }} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.clearFilters')}</button>
            )}
            {(query || range !== 'any' || category !== 'all') && <span className="text-xs text-gray-500">{t('plateTemplates.found', { count: shown.length })}</span>}
          </div>

          {pending > 0 && (
            <p className="inline-flex items-center gap-2 text-xs text-gray-500">
              <FiLoader size={12} className="animate-spin" />
              {t('plateTemplates.preparingAll', { done: data.templates.length - pending, total: data.templates.length })}
            </p>
          )}

          {style === 'created' && ofStyle.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center space-y-3">
              <p className="text-sm text-gray-600">{t('plateTemplates.createdEmpty')}</p>
              <button onClick={() => setCreating(true)} className="btn btn-primary inline-flex items-center gap-2"><FiPlus size={15} /> {t('plateTemplates.create')}</button>
            </div>
          )}

          {shown.length === 0 && ofStyle.length > 0 && (
            <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center">{t('plateTemplates.noMatch')}</p>
          )}

          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {shown.slice(0, visible).map((tp) => (
              <li key={tp.id}>
                <button
                  onClick={() => setOpenId(tp.id)}
                  className="w-full text-left rounded-2xl border border-gray-200 bg-white overflow-hidden hover:border-gray-300 hover:shadow-sm transition"
                >
                  <LoopPreview template={tp} ready={data.ready[tp.id] && (!isMade(tp) || tp.status === 'ready')} failed={Boolean(data.failed?.[tp.id])} className="aspect-video" />
                  <div className="px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-gray-900 truncate">
                        {tp.te || tp.name}{tp.te && tp.name ? <span className="text-gray-400 font-medium"> · {tp.name}</span> : null}
                      </p>
                      <span className="text-[11px] text-gray-500 flex-shrink-0">{t('plateTemplates.loop', { seconds: tp.seconds })}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {tp.custom && <span className="mr-1.5 rounded-full bg-violet-100 text-violet-700 px-1.5 py-px">{t('plateTemplates.customBadge')}</span>}
                      {tp.created && tp.status !== 'ready' && <span className="mr-1.5 rounded-full bg-amber-100 text-amber-800 px-1.5 py-px">{t(`plateTemplates.status.${tp.status}`)}</span>}
                      {label(byCategory[tp.category] || { name: '', te: '' })}
                      {tp.windows > 1 ? ` · ${t('plateTemplates.areas', { count: tp.windows })}` : ''}
                    </p>
                    {/* Who made it, when, and when it last changed (built-in plates come with the app). */}
                    {tp.builtIn ? (
                      <p className="text-[11px] text-gray-400 mt-1">{t('plateTemplates.card.builtIn')}</p>
                    ) : (
                      <div className="text-[11px] text-gray-500 mt-1 space-y-px">
                        {tp.createdByName && <p className="truncate flex items-center gap-1"><FiUser size={10} aria-hidden="true" /> {tp.createdByName}</p>}
                        {tp.createdAt && <p className="truncate">{t('plateTemplates.card.created', { at: when(tp.createdAt) })}</p>}
                        {tp.createdAt && <p className="truncate">{t('plateTemplates.card.modified', { at: when(tp.updatedAt || tp.createdAt) })}</p>}
                      </div>
                    )}
                    {tp.tags?.length > 0 && (
                      <p className="flex flex-wrap gap-1 mt-1.5">
                        {tp.tags.slice(0, 4).map((tag) => <span key={tag} className="rounded-full bg-gray-100 text-gray-600 text-[10px] px-1.5 py-px">{tag}</span>)}
                        {tp.tags.length > 4 && <span className="text-[10px] text-gray-400">+{tp.tags.length - 4}</span>}
                      </p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {visible < shown.length && (
            <div ref={more} className="flex justify-center py-4">
              <button type="button" onClick={() => setVisible((v) => v + PAGE)} className="btn btn-outline btn-sm">{t('plateTemplates.showMore', { count: shown.length - visible })}</button>
            </div>
          )}
        </>
      )}

      {/* An uploaded or created plate that is ready: its own page, to see and change it. */}
      {open && open.uploaded && (open.status || 'ready') === 'ready' && (
        <PlateStudio
          template={open}
          category={byCategory[open.category]}
          categories={data.categories}
          canEdit={Boolean(canRemove(open))}
          onSaved={() => load()}
          onRemove={() => remove(open)}
          onReload={load}
          onClose={() => setOpenId(null)}
          onStep={step}
          renderTags={() => <TagEditor template={open} canEdit={canTag(open)} onSaved={load} />}
          onOpenPrompts={() => { setOpenId(null); setPromptsOpen(true); }}
        />
      )}
      {open && !(open.uploaded && (open.status || 'ready') === 'ready') && (
        <TemplateViewer
          template={open}
          ready={data.ready[open.id] && (!isMade(open) || open.status === 'ready')}
          loopFailed={Boolean(data.failed?.[open.id])}
          category={byCategory[open.category]}
          canRemove={canRemove(open)}
          canTag={canTag(open)}
          onRemove={() => remove(open)}
          onEdit={() => setSettingUp(open)}
          onReload={load}
          onRestyle={async (change) => {
            // A new font or style: save it and show it — the preview with words is redrawn.
            try {
              await fullStoryVideoService.updatePlateTemplate(open.id, change);
              setWithVideo(true);
              await load();
            } catch (e) {
              showError(e);
            }
          }}
          onClose={() => setOpenId(null)}
          onStep={step}
          withVideo={withVideo}
          onToggleVideo={() => setWithVideo((v) => !v)}
        />
      )}

      {uploading && (
        <UploadDialog
          categories={data.categories}
          onClose={() => setUploading(false)}
          onUploaded={(made, someFailed) => {
            if (!someFailed) setUploading(false);
            setStyle('upload');
            setCategory('all');
            load();
            // Check the first one's text area straight away: its page opens.
            if (made[0]?.id) setOpenId(made[0].id);
          }}
        />
      )}

      {settingUp && data && (
        <PlateSetup
          plate={settingUp}
          categories={data.categories}
          onClose={() => setSettingUp(null)}
          onSaved={() => { setSettingUp(null); load(); }}
        />
      )}

      {/* Favourite & recent prompts, from the gallery: "Use" starts a new plate with the prompt. */}
      {promptsOpen && (
        <PromptLibrary
          onUse={(text) => { setPromptsOpen(false); setCreating({ prompt: text }); }}
          onClose={() => setPromptsOpen(false)}
        />
      )}

      {creating && (
        <CreateDialog
          initialPrompt={creating?.prompt || ''}
          categories={data?.categories || []}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setStyle('created'); setCategory('all'); load(); }}
        />
      )}
    </div>
  );
};
